/**
 * Pure helpers for rendering Strategy Sections in Marketing-UI (#936 D4 / D5).
 *
 * Kept free of Angular / DOM imports so they can be unit-tested in the
 * cdp_marketing Jest harness via type re-import or directly in a future
 * Marketing-UI Jest setup.
 */
import type { marketingStrategySectionEntity } from 'mj_generatedentities';

export type StrategySectionRow = marketingStrategySectionEntity;

export interface SectionGroup {
    sectionType: 'Foundation' | 'Plan';
    label: string;
    sections: StrategySectionRow[];
}

/**
 * Group a flat list of Strategy Sections by SectionType. Sorts each group by
 * `DisplayOrder ASC NULLS LAST`. Foundation group is always emitted before
 * Plan (matching the StrategyDocument.Body composition order).
 */
export function groupSectionsByType(sections: readonly StrategySectionRow[]): SectionGroup[] {
    const foundation: StrategySectionRow[] = [];
    const plan: StrategySectionRow[] = [];
    for (const s of sections) {
        if (s.SectionType === 'Foundation') foundation.push(s);
        else if (s.SectionType === 'Plan') plan.push(s);
    }
    const sorter = (a: StrategySectionRow, b: StrategySectionRow): number => {
        const ao = a.DisplayOrder ?? Number.POSITIVE_INFINITY;
        const bo = b.DisplayOrder ?? Number.POSITIVE_INFINITY;
        if (ao !== bo) return ao - bo;
        return (a.Title ?? a.SectionKey).localeCompare(b.Title ?? b.SectionKey);
    };
    return [
        { sectionType: 'Foundation', label: 'Foundations', sections: foundation.slice().sort(sorter) },
        { sectionType: 'Plan', label: 'Plan', sections: plan.slice().sort(sorter) },
    ];
}

/** Filter to only the section rows matching one type. */
export function filterByType(
    sections: readonly StrategySectionRow[],
    sectionType: 'Foundation' | 'Plan',
): StrategySectionRow[] {
    return sections
        .filter(s => s.SectionType === sectionType)
        .slice()
        .sort((a, b) => {
            const ao = a.DisplayOrder ?? Number.POSITIVE_INFINITY;
            const bo = b.DisplayOrder ?? Number.POSITIVE_INFINITY;
            if (ao !== bo) return ao - bo;
            return (a.Title ?? a.SectionKey).localeCompare(b.Title ?? b.SectionKey);
        });
}

/**
 * Color hint for a Policy badge. Maps to the workspace's existing pill-variant
 * vocabulary ('good' | 'warn' | 'bad' | 'muted' | 'info').
 */
export function policyVariant(policy: StrategySectionRow['Policy']): 'good' | 'warn' | 'bad' | 'muted' | 'info' {
    switch (policy) {
        case 'Leadership-Set': return 'info';
        case 'Founder-Approved': return 'good';
        case 'Agent-Proposed-Founder-Approved': return 'warn';
        case 'Agent-Autonomous-Within-Bounds': return 'bad';
        default: return 'muted';
    }
}

/** Color hint for a ConfidenceLevel pill. */
export function confidenceVariant(level: StrategySectionRow['ConfidenceLevel']): 'good' | 'warn' | 'bad' | 'muted' {
    switch (level) {
        case 'Strong': return 'good';
        case 'Moderate': return 'warn';
        case 'Weak': return 'bad';
        default: return 'muted';
    }
}

/**
 * Result type for `parseAutonomousBounds` — distinguishes "no bounds" (the
 * common case for non-autonomous policies) from "parse failed" (which we
 * want to surface so the UI can flag a malformed bounds JSON).
 */
export interface ParsedBoundsResult {
    bounds: Record<string, unknown> | null;
    parseError: string | null;
}

/**
 * Safely parse the AutonomousBoundsJSON field. Returns `bounds=null,
 * parseError=null` for any policy other than Agent-Autonomous-Within-Bounds,
 * or when the field itself is empty. On JSON parse failure, returns
 * `bounds=null, parseError=<message>` so the calling component can render a
 * malformed-bounds warning.
 */
export function parseAutonomousBounds(section: StrategySectionRow): ParsedBoundsResult {
    if (section.Policy !== 'Agent-Autonomous-Within-Bounds') return { bounds: null, parseError: null };
    if (!section.AutonomousBoundsJSON) return { bounds: null, parseError: null };
    try {
        const parsed: unknown = JSON.parse(section.AutonomousBoundsJSON);
        if (parsed !== null && typeof parsed === 'object' && !Array.isArray(parsed)) {
            return { bounds: parsed as Record<string, unknown>, parseError: null };
        }
        return { bounds: null, parseError: 'AutonomousBoundsJSON is not a JSON object' };
    } catch (err: unknown) {
        const msg = err instanceof Error ? err.message : String(err);
        return { bounds: null, parseError: msg };
    }
}
